import { useContext } from 'react';
import InputContext from '../context/InputContext';
import { IFormField, IFormStructure, SelectValue } from '../schema/interface';
import { SET_FIELD } from './reducer';
import DOMPurify from 'dompurify';
import vm from 'vm-browserify';
import { isIsoDate } from '../utils/date';
import FormStructureContext from '../context/FormStructureContext';
import { isFloat, isInt } from '../utils/helper';

const notExistKey = 'I can not find the key with the value';
const selectType = '{choices:[],others}';
const getFuncsError = (fName: string, type: string) => {
    return `${fName} is only used for ${type} fields
  `;
};
const setFuncsError = (fName: string, key: string, value: string) => {
    return `the arguments type of ${fName} is incorrect,
   the arguments of this function are expected to be of key : ${key} and value : ${value} respectively`;
};

interface IAlgorithm {
    formStructure: IFormStructure;
    formValues: { [key: string]: any };
    handleChangeVisible?: (fieldName: string, visible: boolean) => void;
}
interface IHandleAlgorithm {
    handleAlgorithmCode: () => void;
}
const useAlgorithm = ({
    formStructure,
    formValues,
    handleChangeVisible
}: IAlgorithm): IHandleAlgorithm => {
    const { dispatch } = useContext(InputContext);
    const { setErrorMessage } = useContext(FormStructureContext);

    // get key and convert to the field name
    const getName = (key: string) => {
        if (typeof key !== 'string') return undefined;
        const field = formStructure?.fields?.find((item: IFormField) => item?.key === key);
        if (field?.name) {
            return field?.name;
        }
    };

    const isInvisible = (key: string) => {
        if (typeof key !== 'string') return undefined;
        const field = formStructure?.fields?.find((item: IFormField) => item?.key === key);
        if (field?.presentation?.invisible) {
            return true;
        }
        return false;
    };

    const validationGetFunctions = (key: string) => {
        if (typeof key !== 'string') return undefined;
        const fieldName = getName(key);
        if (!fieldName) {
            setErrorMessage(`${notExistKey} : ${key}`);
            return;
        }
        return fieldName;
    };

    const validationSetFunctions = (key: string) => {
        const name = getName(key);
        if (!name) {
            setErrorMessage(`${notExistKey} : ${key}`);
            return;
        }
        return name;
    };

    const getText = (key: string) => {
        const fieldName = validationGetFunctions(key);
        const fieldValue = formValues?.[fieldName];
        if (fieldValue) {
            if (typeof fieldValue !== 'string' || !Number.isNaN(+fieldValue)) {
                setErrorMessage(getFuncsError('getText', 'string'));
                return null;
            }
        }
        return fieldValue;
    };

    const setText = (key: string, value: string) => {
        const fieldName = validationSetFunctions(key);
        if (
            typeof key !== 'string' ||
            typeof value !== 'string' ||
            (value && !Number.isNaN(+value))
        ) {
            setErrorMessage(setFuncsError('setText', 'string', 'string'));
            return;
        }
        if (value) {
            setTimeout(() => {
                dispatch({
                    type: SET_FIELD,
                    name: fieldName,
                    value
                });
            }, 0);
        }
    };

    const getNumber = (key: string) => {
        const fieldName = validationGetFunctions(key);
        const fieldValue = formValues?.[fieldName];
        const value = isInt(fieldValue)
            ? Number(fieldValue)
            : isFloat(fieldValue)
            ? parseFloat(fieldValue)
            : '';
        if (fieldValue) {
            if (typeof value !== 'number' || Number.isNaN(value)) {
                setErrorMessage(getFuncsError('getNumber', 'number'));
                return null;
            }
        }
        return value;
    };

    const setNumber = (key: string, value: number) => {
        const fieldName = validationSetFunctions(key);
        if (typeof key !== 'string' || (value && typeof value !== 'number')) {
            setErrorMessage(setFuncsError('setNumber', 'string', 'number'));
            return;
        }
        if (value) {
            setTimeout(() => {
                dispatch({
                    type: SET_FIELD,
                    name: fieldName,
                    value: value.toString()
                });
            }, 0);
        }
    };

    const getSingleSelect = (key: string) => {
        const fieldName = validationGetFunctions(key);
        console.log(`getSingleSelect: fieldName: ${fieldName}`);
        const fieldValue = formValues?.[fieldName];
        console.log(`getSingleSelect: fieldValue: ${fieldValue}`);
        const choices = fieldValue?.choices;
        console.log(`getSingleSelect: choices: ${choices}`);
        if (!(choices instanceof Array)) return null;
        const choiceValueLength = 2;  // represents a tuple of [key, label]
        if (!(fieldValue instanceof Array && fieldValue.length === choiceValueLength)) {
            setErrorMessage(getFuncsError('getSingleSelect', 'singleSelect'));
            return;
        }
        return fieldValue;
    };

    const setSingleSelect = (key: string, value: SelectValue) => {
        const fieldName = validationSetFunctions(key);
        if (typeof key !== 'string' || (value && !(value && value?.choices && value?.others))) {
            setErrorMessage(setFuncsError('setSingleSelect', 'string', selectType));
            return;
        }

        if (value?.choices?.length || value?.others?.length) {
            setTimeout(() => {
                dispatch({
                    type: SET_FIELD,
                    name: fieldName,
                    value
                });
            }, 0);
        }
    };

    const getMultiSelect = (key: string) => {
        const fieldName = validationGetFunctions(key);
        const fieldValue = formValues?.[fieldName];
        const choices = fieldValue?.choices;
        const isArray =
            choices?.[0] instanceof Array
                ? choices?.[0] instanceof Array
                : choices instanceof Array;
        if (!choices || !isArray) {
            setErrorMessage(getFuncsError('getMultiSelect', 'multiSelect'));
            return;
        }
        return fieldValue;
    };

    const setMultiSelect = (key: string, value: SelectValue) => {
        const fieldName = validationSetFunctions(key);
        if (typeof key !== 'string' || (value && !(value && value?.choices && value?.others))) {
            setErrorMessage(setFuncsError('setSingleSelect', 'string', selectType));
            return;
        }

        if (value?.choices?.length || value?.others?.length) {
            setTimeout(() => {
                dispatch({
                    type: SET_FIELD,
                    name: fieldName,
                    value
                });
            }, 0);
        }
    };

    const isTrue = (key: string) => {
        const fieldName = validationGetFunctions(key);
        const fieldValue = formValues?.[fieldName];
        const choices = fieldValue?.choices;
        const isArray =
            choices?.[0] instanceof Array
                ? choices?.[0] instanceof Array
                : choices instanceof Array;
        if (!choices || !isArray) {
            setErrorMessage(getFuncsError('isTrue', 'booleanSelect'));
            return;
        }
        if (choices?.[0] instanceof Array) {
            return choices?.[0][0] === '1';
        }
        return choices?.[0] === '1';
    };

    const setBoolean = (key: string, value: boolean) => {
        const fieldName = validationSetFunctions(key);
        if (typeof key !== 'string' || typeof value !== 'boolean' || !value) {
            setErrorMessage(setFuncsError('setBoolean', 'string', 'boolean'));
            return;
        }
        const fieldValue: SelectValue = { choices: value ? ['1'] : [], others: [] };
        setTimeout(() => {
            dispatch({
                type: SET_FIELD,
                name: fieldName,
                value: fieldValue
            });
        }, 0);
    };

    const getDate = (key: string) => {
        const fieldName = validationGetFunctions(key);
        const fieldValue = formValues?.[fieldName];
        if (fieldValue) {
            if (Number.isNaN(Date.parse(fieldValue))) {
                setErrorMessage(getFuncsError('getDate', 'date'));
                return null;
            }
        }
        return fieldValue;
    };

    const setDate = (key: string, value: string) => {
        const fieldName = validationSetFunctions(key);
        if (typeof key !== 'string' || Number.isNaN(Date.parse(value))) {
            setErrorMessage(setFuncsError('setDate', 'string', 'date'));
            return;
        }
        if (value) {
            setTimeout(() => {
                dispatch({
                    type: SET_FIELD,
                    name: fieldName,
                    value
                });
            }, 0);
        }
    };
    const getDatetime = (key: string) => {
        const fieldName = validationGetFunctions(key);
        const fieldValue = formValues?.[fieldName];
        if (fieldValue) {
            if (Number.isNaN(Date.parse(fieldValue))) {
                setErrorMessage(getFuncsError('getDatetime', 'date'));
                return null;
            }
        }
        return fieldValue;
    };

    const setDatetime = (key: string, value: string) => {
        const fieldName = validationSetFunctions(key);
        if (typeof key !== 'string' || !isIsoDate(value)) {
            setErrorMessage(setFuncsError('setDatetime', 'string', 'date'));
            return;
        }
        if (isIsoDate(value)) {
            setTimeout(() => {
                dispatch({
                    type: SET_FIELD,
                    name: fieldName,
                    value
                });
            }, 0);
        }
    };

    const hide = (key: string) => {
        if (typeof key !== 'string') return;
        if (isInvisible(key)) return;
        const fieldName = getName(key);
        handleChangeVisible(fieldName, true);
    };

    const unhide = (key: string) => {
        if (typeof key !== 'string') return;
        if (!isInvisible(key)) return;
        const fieldName = getName(key);
        handleChangeVisible(fieldName, false);
    };

    const context: { [key: string]: Function } = {
        getText,
        setText,
        getNumber,
        setNumber,
        getSingleSelect,
        setSingleSelect,
        getMultiSelect,
        setMultiSelect,
        isTrue,
        setBoolean,
        getDate,
        setDate,
        getDatetime,
        setDatetime,
        hide,
        unhide
    };

    const handleAlgorithmCode = () => {
        try {
            if (formStructure?.algorithm) {
                const sanitizedCode = DOMPurify.sanitize(formStructure?.algorithm);
                vm.runInNewContext(sanitizedCode, context);
            }
        } catch (err) {
            const errorMessage = err?.stack?.substr(0, err?.stack?.indexOf('\n'));
            setErrorMessage(errorMessage);
            console.log('algorithm error:', err);
        }
    };

    return {
        handleAlgorithmCode
    };
};

export default useAlgorithm;
